import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AdditionalSkills, Competences, DriveLicense, Education, LanguageCompetence, Training } from "../../entities/Competences";
import { EducationParts } from "../../entities/EducationParts";
import i18n from "../../i18n";
import { apiCallBegan, ApiCallFailedPayload, ApiCallSuccessPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import ResponseBase from "../types/ResponseBase";
import SliceSingletonBase from "../types/SliceSingletonBase";
import { authHeader, getWorkerId } from "../users/auth";

export interface CompetencesResponse extends ResponseBase {
    WorkerId: number;
    DriveLicense: DriveLicense | null;
    LanguageCompetences: LanguageCompetence[];
    EducationHistory: Education[];
    TrainingHistory: Training[];
    AdditionalSkills?: AdditionalSkills;
}

interface State {
    forRequesting: {
    } & SliceSingletonBase<Competences>;
    forPosting: {
    } & SliceSingletonBase<Competences>;
    forEducationParts: {
    } & SliceSingletonBase<EducationParts>;
    forEducationPartsPosting: {
    } & SliceSingletonBase<null>;
}

const initialState: State = {
    forRequesting: {
        data: null,
        loading: false,
        lastFetch: null,
        error: null
    },
    forPosting: {
        data: null,
        loading: false,
        lastFetch: null,
        error: null
    },
    forEducationParts: {
        data: null,
        loading: false,
        lastFetch: null,
        error: null
    },
    forEducationPartsPosting: {
        data: null,
        loading: false,
        lastFetch: null,
        error: null
    }
};

const slice = createSlice({
    name: "competences",
    initialState,
    reducers: {
        competencesRequested: competences => {
            competences.forRequesting.loading = true;
            competences.forRequesting.error = null;
        },
        competencesReceived: (
            competences,
            action: PayloadAction<CompetencesResponse>
        ) => {
            competences.forRequesting.data = schema(action.payload);
            competences.forRequesting.loading = false;
            competences.forRequesting.lastFetch = Date.now();
        },
        competencesRequestFailed: (
            competences,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            competences.forRequesting.loading = false;
            competences.forRequesting.error = action.payload;
        },

        competencesChangePosted: (
            competences) => {
            competences.forPosting.loading = true;
            competences.forPosting.error = null;
        },
        competencesChangePostSuccess: (
            competences, action: PayloadAction<ApiCallSuccessPayload>
        ) => {
            competences.forPosting.loading = false;
            competences.forPosting.error = null;
        },
        competencesChangePostFailed: (
            competences, action: PayloadAction<ApiCallFailedPayload>
        ) => {
            competences.forPosting.loading = false;
            competences.forPosting.error = action.payload;
        },

        educationPartsRequested: competences => {
            competences.forEducationParts.loading = true;
            competences.forEducationParts.error = null;
        },
        educationPartsReceived: (
            competences,
            action: PayloadAction<EducationParts>
        ) => {
            competences.forEducationParts.data = schemaEducationParts(action.payload);
            competences.forEducationParts.loading = false;
            competences.forEducationParts.lastFetch = Date.now();
        },
        educationPartsRequestFailed: (
            competences,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            competences.forEducationParts.loading = false;
            competences.forEducationParts.error = action.payload;
        },

        educationPartsPosted: competences => {
            competences.forEducationPartsPosting.loading = true;
            competences.forEducationPartsPosting.error = null;
        },
        educationPartsPostSuccess: (
            competences,
            action: PayloadAction<ApiCallSuccessPayload>
        ) => {
            competences.forEducationPartsPosting.loading = false;
            competences.forEducationPartsPosting.error = null;
            // Clear cached data to trigger refresh on next getEducationParts
            competences.forEducationParts.data = null;
        },
        educationPartsPostFailed: (
            competences,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            competences.forEducationPartsPosting.loading = false;
            competences.forEducationPartsPosting.error = action.payload;
        }
    }
});

const {
    competencesRequested,
    competencesReceived,
    competencesRequestFailed,
    competencesChangePosted,
    competencesChangePostSuccess,
    competencesChangePostFailed,
    educationPartsRequested,
    educationPartsReceived,
    educationPartsRequestFailed,
    educationPartsPosted,
    educationPartsPostSuccess,
    educationPartsPostFailed
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
const CACHE_DURATION = 3600 * 1000; // 1 hour in milliseconds

export const getWorkerCompetences =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.competences.forRequesting.loading) {
            return;
        }
        const workerId = getWorkerId(getState());
        if (!workerId) {
            return;
        }

        const url = "/api/competences/" + workerId;

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },
                onStart: competencesRequested.type,
                onSuccess: competencesReceived.type,
                onError: competencesRequestFailed.type
            })
        );
    };

export const changeCompetences =
    (competences: Competences) =>
        async (dispatch: AppDispatch, getState: () => AppState) => {
            if (getState().entities.competences.forPosting.loading) {
                return;
            }
            const workerId = getWorkerId(getState());
            if (!workerId || competences.WorkerId !== workerId) {
                return;
            }

            dispatch(
                apiCallBegan({
                    url: "/api/competences",
                    method: "POST",
                    headers: {
                        ...authHeader(),
                        Accept: "*/*",
                        "Content-Type": "application/json"
                    },
                    data: JSON.stringify({
                        WorkerId: competences.WorkerId,
                        DriveLicense: competences.DriveLicense,
                        LanguageCompetences: competences.LanguageCompetences,
                        EducationHistory: competences.EducationHistory,
                        TrainingHistory: competences.TrainingHistory,
                        AdditionalSkills: competences.AdditionalSkills
                    }),
                    onStart: competencesChangePosted.type,
                    onSuccess: competencesChangePostSuccess.type,
                    onError: competencesChangePostFailed.type
                })
            );
        };

export const getEducationParts =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        const { forEducationParts } = getState().entities.competences;
        if (forEducationParts.loading ||
            (forEducationParts.data && forEducationParts.lastFetch &&
                Date.now() - forEducationParts.lastFetch < CACHE_DURATION)) {
            return;
        }

        const url = "/api/competences/education-parts";

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },
                onStart: educationPartsRequested.type,
                onSuccess: educationPartsReceived.type,
                onError: educationPartsRequestFailed.type
            })
        );
    };

export const postEducationParts =
    (educationParts: EducationParts) =>
        async (dispatch: AppDispatch, getState: () => AppState) => {
            if (getState().entities.competences.forEducationPartsPosting.loading) {
                return;
            }

            const url = "/api/competences/education-parts";

            // Filter items with Id == 0
            const newEducationParts: EducationParts = {
                Institutions: educationParts.Institutions.filter(item => item.Id === 0),
                Specializations: educationParts.Specializations.filter(item => item.Id === 0),
                CertificateTypes: educationParts.CertificateTypes.filter(item => item.Id === 0)
            };

            // Skip if no new items
            if (
                newEducationParts.Institutions.length === 0 &&
                newEducationParts.Specializations.length === 0 &&
                newEducationParts.CertificateTypes.length === 0
            ) {
                return;
            }

            return await dispatch(
                apiCallBegan({
                    url,
                    method: "POST",
                    headers: {
                        ...authHeader(),
                        Accept: "*/*",
                        "Content-Type": "application/json"
                    },
                    data: JSON.stringify(newEducationParts),
                    onStart: educationPartsPosted.type,
                    onSuccess: educationPartsPostSuccess.type,
                    onError: educationPartsPostFailed.type
                })
            );
        };

export const schema = (response: CompetencesResponse): Competences => {
    return {
        WorkerId: response.WorkerId,
        DriveLicense: response.DriveLicense ? {
            ...response.DriveLicense,
            ExpirationDate: response.DriveLicense.ExpirationDate
        } : null,
        LanguageCompetences: (response.LanguageCompetences || []).map(lang => ({
            ...lang,
            Level: lang.Level
        })),
        EducationHistory: (response.EducationHistory || []).map(edu => ({
            ...edu,
            GraduationDate: edu.GraduationDate,
            Certificate: edu.Certificate ? {
                ...edu.Certificate,
                ExpirationDate: edu.Certificate.ExpirationDate
            } : undefined
        })),
        TrainingHistory: (response.TrainingHistory || []).map(training => ({
            ...training,
            StartDate: training.StartDate,
            EndDate: training.EndDate,
            Certificate: training.Certificate ? {
                ...training.Certificate,
                ExpirationDate: training.Certificate.ExpirationDate
            } : undefined
        })),
        AdditionalSkills: response.AdditionalSkills
    };
};

export const schemaEducationParts = (response: EducationParts): EducationParts => {
    return {
        Institutions: response.Institutions || [],
        Specializations: response.Specializations || [],
        CertificateTypes: response.CertificateTypes || []
    };
};